import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const CHAPTERS = [
  {
    num: "01",
    title: "Discovery & Strategy",
    desc: "We dig into your idea, your users, and your goals — then shape a clear product roadmap with defined scope, priorities, and success metrics.",
    chips: ["Product roadmap", "Scope definition", "Technical feasibility"],
  },
  {
    num: "02",
    title: "Architecture & Design",
    desc: "Wireframes become polished interfaces. We design the system architecture and the user experience side by side, so nothing gets lost in translation.",
    chips: ["UX wireframes", "Visual design", "System architecture"],
  },
  {
    num: "03",
    title: "High-Velocity Engineering",
    desc: "Tight build cycles with working software you can touch early and often. Clean code, honest communication, and momentum you can feel.",
    chips: ["Iterative builds", "Code review", "Continuous demos"],
  },
  {
    num: "04",
    title: "Deployment & Scale",
    desc: "Launch day is the starting line. We ship to app stores and production, monitor performance, and iterate as your user base grows.",
    chips: ["Store submission", "Monitoring", "Ongoing iteration"],
  },
];

export const Process = () => (
  <section id="process" data-testid="process-section" className="relative py-24 lg:py-32 bg-[#17181c] border-y border-zinc-800/60">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-teal-400">
          02 — How we work
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-50 max-w-2xl">
          Four chapters from idea to launch.
        </h2>
      </Reveal>

      <div className="mt-16 flex flex-col">
        {CHAPTERS.map((c, i) => (
          <motion.div
            key={c.num}
            data-testid={`process-chapter-${c.num}`}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="group relative grid md:grid-cols-12 gap-6 md:gap-10 py-10 lg:py-14 border-t border-zinc-800 first:border-t-0 items-start"
          >
            <div className="md:col-span-3 flex items-baseline gap-4">
              <span className="font-mono text-5xl lg:text-7xl font-semibold text-outline transition-all duration-500 group-hover:[-webkit-text-stroke:1px_rgba(45,212,191,0.7)]">
                {c.num}
              </span>
              <span className="hidden md:block h-px flex-1 bg-zinc-800 group-hover:bg-teal-500/50 transition-colors duration-500" />
            </div>
            <div className="md:col-span-5">
              <h3 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight text-zinc-50 group-hover:text-teal-300 transition-colors duration-500">
                {c.title}
              </h3>
              <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-md">
                {c.desc}
              </p>
            </div>
            <div className="md:col-span-4 flex flex-wrap gap-2 md:justify-end content-start">
              {c.chips.map((chip) => (
                <span
                  key={chip}
                  className="font-mono text-[11px] uppercase tracking-wider text-zinc-400 border border-zinc-700/80 rounded-full px-3.5 py-1.5 group-hover:border-teal-500/40 group-hover:text-teal-300/90 transition-all duration-500"
                >
                  {chip}
                </span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
